import { Link } from "wouter";
import { Phone, Mail, Clock, AlertCircle, Zap, ArrowRight } from "lucide-react";
import { trackEvent } from "@/hooks/use-analytics";
import { GetInTouch } from "@/components/sections/get-in-touch";
import { Accreditations } from "@/components/sections/accreditations";
import logoSrc from "/images/brand/vertex-logo.png";
import { saunaServices } from "@/data/sauna-services";

export function Footer() {
  return (
    <>
    <section id="electrical-work" aria-labelledby="electrical-work-title" className="bg-slate-950 text-white py-8 scroll-mt-28">
      <div className="container mx-auto max-w-7xl px-4 flex flex-col md:flex-row md:items-center gap-5">
        <span className="grid place-items-center w-12 h-12 shrink-0 rounded-xl bg-sky-400/15 text-sky-300"><Zap aria-hidden="true" className="w-6 h-6" /></span>
        <div className="flex-1">
          <h2 id="electrical-work-title" className="font-display text-xl md:text-2xl font-bold mb-2">Fully qualified electricians. Small jobs to complete projects.</h2>
          <p className="text-slate-300 leading-relaxed max-w-3xl">Our fully qualified electricians support projects of every size. We carry out complete installations, including the electrical work, bringing air conditioning, HVAC, sauna systems and their electrical requirements together in one agreed project scope.</p>
        </div>
        <Link href="/contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-950 font-semibold px-5 py-3 shrink-0 hover:bg-sky-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky-300">
          Discuss your project <ArrowRight aria-hidden="true" className="w-4 h-4" />
        </Link>
      </div>
    </section>
    <Accreditations />
    <GetInTouch />
    <footer className="bg-foreground text-background py-16 border-t border-white/10">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <img src={logoSrc} alt="VertexAir Climate Systems" className="h-8 w-auto brightness-0 invert" />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Premium air conditioning and HVAC solutions for London's most discerning residential and commercial clients.
            </p>
            <div className="text-slate-400 text-xs leading-relaxed space-y-1">
              <p className="font-semibold text-slate-300">Elecwest Electrical Contractors Ltd</p>
              <p>Company Registration Number: 16093679</p>
              <p>Registered Office: Elecwest Electrical Contractors Ltd</p>
              <p>13 St. Swithin's Lane - 2nd Floor - London - EC4N 8AL</p>
              <p>NICEIC Approved Contractor 612866 - Gas Safe Register 963183</p>
            </div>
          </div>
          
          <div>
            <h3 className="font-bold text-white mb-6 uppercase tracking-wider text-sm">Air Conditioning</h3>
            <ul className="space-y-3">
              <li><Link href="/installation"><span className="text-slate-400 hover:text-white transition-colors text-sm cursor-pointer block">Installation</span></Link></li>
              <li><Link href="/repair"><span className="text-slate-400 hover:text-white transition-colors text-sm cursor-pointer block">Repairs</span></Link></li>
              <li><Link href="/maintenance"><span className="text-slate-400 hover:text-white transition-colors text-sm cursor-pointer block">Maintenance</span></Link></li>
              <li><Link href="/case-studies" className="text-slate-400 hover:text-white transition-colors text-sm block" data-testid="link-footer-case-studies">Case studies</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-white mb-6 uppercase tracking-wider text-sm">HVAC Systems</h3>
            <ul className="space-y-3">
              <li><Link href="/hvac/ventilation"><span className="text-slate-400 hover:text-white transition-colors text-sm cursor-pointer block">Ventilation</span></Link></li>
              <li><Link href="/hvac/heating"><span className="text-slate-400 hover:text-white transition-colors text-sm cursor-pointer block">Heating</span></Link></li>
              <li><Link href="/hvac/ductwork"><span className="text-slate-400 hover:text-white transition-colors text-sm cursor-pointer block">Ductwork</span></Link></li>
              <li><Link href="/hvac/controls"><span className="text-slate-400 hover:text-white transition-colors text-sm cursor-pointer block">BMS & Controls</span></Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold text-white mb-6 uppercase tracking-wider text-sm">Sauna</h3>
            <ul className="space-y-3">
              {saunaServices.map(s => <li key={s.slug}><Link href={s.href} className="text-slate-400 hover:text-white text-sm">{s.slug === "install" ? "Sauna installation" : s.navTitle}</Link></li>)}
              <li><Link href="/sauna/guides" className="text-slate-400 hover:text-white text-sm">Sauna guides</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-white mb-6 uppercase tracking-wider text-sm">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-primary shrink-0" />
                <a href="tel:02080870906" className="text-slate-400 hover:text-white transition-colors text-sm" onClick={() => trackEvent("call_click", "Footer Phone")}>020 8087 0906</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-primary shrink-0" />
                <a href="mailto:info@vertexclimate.co.uk" className="text-slate-400 hover:text-white transition-colors text-sm">info@vertexclimate.co.uk</a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-primary shrink-0" />
                <span className="text-slate-400 text-sm">8:45am–5pm Mon–Fri</span>
              </li>
              <li className="flex items-center gap-3">
                <AlertCircle className="w-4 h-4 text-primary shrink-0" />
                <span className="text-slate-400 text-sm">24/7 Emergency Line</span>
              </li>
            </ul>
          </div>
          
        </div>
        
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            &copy; 2026 Vertex Climate Systems t/a Elecwest Electrical Contractors Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
    </>
  );
}
